import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';
import { Alert, Paragraph } from '@digdir/designsystemet-react';
import { AnalyzeSurveyResultTips } from '../../api/queries';
import { AnalyzeQuestion } from '../../components/AnalyzeQuestion/AnalyzeQuestion';
import { AnalyzeSurveyNavigation } from '../../components/AnalyzeSurveyNavigation/AnalyzeSurveyNavigation';
import { AnalysisLayout } from '../../components/AnalysisLayout/AnalysisLayout';
import { normalizeQuestion } from '../../components/AnalysisLayout/analysis.utils';
import { useGetAnalyzeSurvey } from '../../hooks/queries/useGetAnalyzeSurvey';
import { useAnalyzationSummaryQuery } from '../../hooks/queries/useAnalyzationSummaryQuery/useAnalyzationSummaryQuery';
import { Spinner } from '../../components/Spinner';
import { NotReady } from '../../components/NotReady/NotReady.tsx';
import classes from './AnalyzeSurvey.module.css';

export type CurrentQuestionAnalyzed = AnalyzeSurveyResultTips;

export const AnalyzeSurvey = () => {
  const { t } = useTranslation();
  const { teamId, pageQuestionIndex, surveyId } = useParams();
  const questionNumber = Number(pageQuestionIndex);

  const { data: analyzedSurvey, isLoading, error } = useGetAnalyzeSurvey(parseInt(surveyId || '', 10));
  // The comparison with the previous survey gives "since last time" per question
  const { data: summary } = useAnalyzationSummaryQuery(Number(teamId), Number(surveyId));

  if (isLoading) {
    return <Spinner label={t('analyzeSurvey.analyzing')} />;
  }

  if (analyzedSurvey && analyzedSurvey.code === 'NOT_COMPLETED') {
    return (
      <div className={classes.notReadyContainer}>
        <NotReady
          title={t('analyzeSurvey.notReadyTitle')}
          description={t('analyzeSurvey.notReadyDescription')}
          showNavigateButton
        />
      </div>
    );
  }

  if (analyzedSurvey && analyzedSurvey.data) {
    // Old links or hand-typed URLs can point past the questions; start at the first one
    const total = analyzedSurvey.data.length;
    const basePath = `/teams/${teamId}/analyze-survey/${surveyId}`;
    if (!Number.isInteger(questionNumber) || questionNumber < 1 || questionNumber > total) {
      return <Navigate to={total > 0 ? `${basePath}/1` : `${basePath}/summary`} replace />;
    }

    const current: CurrentQuestionAnalyzed = {
      ...analyzedSurvey.data[questionNumber - 1],
      questionNumber,
    };
    const comparison = summary?.SurveyQuestionScoreComparison?.find(
      (item) => normalizeQuestion(item.question) === normalizeQuestion(current.question ?? ''),
    );

    return (
      <AnalysisLayout questions={analyzedSurvey.data.map((q) => q.question)} step={questionNumber}>
        <AnalyzeQuestion question={current} change={comparison?.difference} />
        <AnalyzeSurveyNavigation questionsTotalLength={analyzedSurvey.data.length} />
      </AnalysisLayout>
    );
  }

  if (error) {
    return (
      <AnalysisLayout step={questionNumber}>
        <Alert data-color="danger">
          <Paragraph>{error.message}</Paragraph>
        </Alert>
      </AnalysisLayout>
    );
  }

  return (
    <>
      <Spinner />
      <p>{t('analyzeSurvey.loading')}</p>
    </>
  );
};
